import { useFrame } from '@react-three/fiber'
import { Howler } from 'howler'
import { useEffect, useRef, useState } from 'react'
import useSound from 'use-sound'

import { LOCO_DEFAULT_SPEED, VELOCITY_EPSILON } from '@engine'
import { GamePhase, useGameStore, useSoundBoard, type ReturnedValue } from '@stores'

const parse = ([play, data]: ReturnedValue) => ({ play, ...data })

const CHUG_BASE_INTERVAL = 200
const CHUG_DURATION = 0.45

const CHUG_POOL_SIZE = 8
let _chugPool: AudioBuffer[] | null = null
let _chugPoolIndex = 0

function getChugBuffer(ctx: AudioContext): AudioBuffer {
  if (!_chugPool) {
    const bufferSize = Math.floor(ctx.sampleRate * CHUG_DURATION)
    _chugPool = Array.from({ length: CHUG_POOL_SIZE }, () => {
      const buf = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
      const data = buf.getChannelData(0)
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1
      return buf
    })
  }
  const buf = _chugPool[_chugPoolIndex]
  _chugPoolIndex = (_chugPoolIndex + 1) % CHUG_POOL_SIZE
  return buf
}

function playChug() {
  const ctx = Howler.ctx
  const destination = Howler.masterGain

  const now = ctx.currentTime

  const noise = ctx.createBufferSource()
  noise.buffer = getChugBuffer(ctx)

  const bp = ctx.createBiquadFilter()
  bp.type = 'bandpass'
  bp.frequency.value = 800
  bp.Q.value = 0.4

  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0.001, now)
  gain.gain.linearRampToValueAtTime(0.5, now + 0.05)
  gain.gain.exponentialRampToValueAtTime(0.001, now + CHUG_DURATION)

  noise.connect(bp)
  bp.connect(gain)
  gain.connect(destination)
  noise.start(now)
  noise.stop(now + CHUG_DURATION)
}

export function SoundBoard() {
  const phase = useGameStore(state => state.phase)
  const paused = useGameStore(state => state.paused)
  const levelComplete = useGameStore(state => state.levelComplete)

  const setContext = useSoundBoard(state => state.setContext)
  const setSounds = useSoundBoard(state => state.setSounds)
  const muted = useSoundBoard(state => state.muted)

  const [loaded, setLoaded] = useState(0)
  const onload = () => setLoaded(loaded => loaded + 1)

  const sounds = {
    brake: parse(useSound('/sounds/brake.mp3', { volume: 0.1, playbackRate: 1.1, onload })),
    bump: parse(useSound('/sounds/bump.mp3', { volume: 0.5, playbackRate: 1.1, onload })),
    couple: parse(useSound('/sounds/couple.mp3', { volume: 0.3, onload })),
    crossingBell: parse(useSound('/sounds/crossing-bell.mp3', { volume: 0.5, onload })),
    loop: parse(useSound('/sounds/loop.mp3', { loop: true, volume: 0.4, onload })),
    switch: parse(useSound('/sounds/switch.mp3', { volume: 0.3, onload })),
    uncouple: parse(useSound('/sounds/uncouple.mp3', { volume: 0.3, onload })),
    whistle: parse(useSound('/sounds/whistle.mp3', { onload })),
  }

  const toLoad = Object.keys(sounds).length
  const lastChugTime = useRef(0)
  const prevMaxSpeed = useRef(0)
  const prevCouplingCount = useRef(0)
  const prevBumpBlocked = useRef<'forward' | 'backward' | undefined>(undefined)
  const braking = useRef(false)
  const brakeId = useRef<number | undefined>(undefined)
  const bellId = useRef<number | undefined>(undefined)
  const pageVisible = useRef(!document.hidden)

  const stopBrake = () => {
    const s = sounds?.brake.sound
    if (brakeId.current !== undefined && s) {
      s.fade(s.volume(brakeId.current) as number, 0, 200, brakeId.current)
      s.once('fade', () => s.stop(brakeId.current), brakeId.current)
      brakeId.current = undefined
    }
  }

  const stopBell = () => {
    const s = sounds?.crossingBell.sound
    if (bellId.current !== undefined && s) {
      s.fade(s.volume(bellId.current) as number, 0, 1500, bellId.current)
      s.once('fade', () => s.stop(bellId.current), bellId.current)
      bellId.current = undefined
    }
  }

  useEffect(() => {
    if (loaded < toLoad) return
    setContext(Howler.ctx)
    setSounds(sounds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, toLoad])

  useEffect(() => {
    if (paused) {
      sounds.loop.stop()
      return
    }

    if (phase >= GamePhase.TITLE && !sounds.loop.sound?.playing()) {
      sounds.loop.play()
    } else {
      prevMaxSpeed.current = 0
      lastChugTime.current = 0
      braking.current = false
      stopBrake()
    }

    prevCouplingCount.current = useGameStore.getState().network?.couplingCount ?? 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, paused])

  useEffect(() => {
    if (!levelComplete) return
    stopBrake()
    braking.current = false
    sounds.whistle.play()
    bellId.current = sounds.crossingBell.sound?.play()
    return () => stopBell()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelComplete])

  useFrame(({ clock }) => {
    const { network, levelComplete, phase, paused } = useGameStore.getState()
    if (!sounds || !network || paused) return

    const now = clock.getElapsedTime() * 1000
    if (!pageVisible.current) {
      lastChugTime.current = now
      return
    }

    if (lastChugTime.current === Infinity) lastChugTime.current = now

    if (network.couplingCount !== prevCouplingCount.current) {
      prevCouplingCount.current = network.couplingCount
      sounds.couple.play()
    }

    if (
      network.bumpBlocked &&
      !prevBumpBlocked.current &&
      !sounds.bump.sound?.playing() &&
      !sounds.couple.sound?.playing()
    ) {
      sounds.bump.play()
    }
    prevBumpBlocked.current = network.bumpBlocked

    const velocities = [
      network.locomotive?.currentVelocity ?? 0,
      ...Array.from(network.npcs?.values() ?? []).map(npc => npc.currentVelocity),
    ]
    const maxSpeed = Math.max(...velocities.map(Math.abs))

    if (maxSpeed > VELOCITY_EPSILON) {
      const interval = CHUG_BASE_INTERVAL * (LOCO_DEFAULT_SPEED / maxSpeed)
      if (now - lastChugTime.current >= interval) {
        if (phase > GamePhase.LOADING) playChug()
        lastChugTime.current = now
      }

      const decelerating = maxSpeed < prevMaxSpeed.current - 0.001
      const accelerating = maxSpeed > prevMaxSpeed.current + 0.001
      if (
        decelerating &&
        !braking.current &&
        prevMaxSpeed.current >= LOCO_DEFAULT_SPEED * 0.5 &&
        !network.bumpBlocked
      ) {
        if (!levelComplete && phase >= GamePhase.TITLE) {
          brakeId.current = sounds.brake.sound?.play()
          braking.current = true
        }
      } else if (accelerating && braking.current) {
        stopBrake()
        braking.current = false
      }
    } else {
      if (
        !braking.current &&
        prevMaxSpeed.current >= LOCO_DEFAULT_SPEED * 0.5 &&
        !network.bumpBlocked
      ) {
        if (!levelComplete && phase >= GamePhase.TITLE) sounds.brake.sound?.play()
      } else {
        stopBrake()
      }
      braking.current = false
    }

    prevMaxSpeed.current = maxSpeed
  })

  useEffect(() => {
    Howler.volume(muted ? 0 : 1)
  }, [muted])

  useEffect(() => {
    const handler = () => {
      pageVisible.current = !document.hidden
      if (document.hidden) {
        Howler.ctx?.suspend()
      } else {
        lastChugTime.current = Infinity
        if (!muted) Howler.ctx?.resume()
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [muted])

  return <></>
}
